<script setup lang="tsx">
import { computed, ref } from 'vue'
import LoadingContainer from '../widgets/LoadingContainer.vue'
import type { DocTreeNode, Document, URL, MoveDocumentRequest, ReferenceConnection } from '@/models/api-generated'
import IconButton from '../shared/IconButton.vue'
import ConnectionsModal from './ConnectionsModal.vue'
import { hasPermission, formatDate, formatBytes, viewing } from '@/services/utils'
import http from '@/services/http'
import sliderStore from '@/stores/slider'
import toastStore from '@/stores/toast'
import { useI18n } from 'vue-i18n'
import { PermDocumentAdd, PermDocumentDelete, PermDocumentWrite } from '@/models/enums-generated'
import { folderRefreshCallback, historyRefreshCallback } from './shared'
import { toAbsoluteUrl } from '@/services/utils'
import DirectorySelectDialog from './DirectorySelectDialog.vue'

const { t } = useI18n()
const model = defineModel<DocTreeNode>()
const isLoading = ref(false)
const slider = sliderStore()
const toaster = toastStore()
const props = defineProps<{ showSelect?: boolean; filterType?: 'all' | 'document' | 'url'; showHistory?: boolean }>()
const lengthToShow = 40

const isDirectorySelectOpen = ref(false)
const selectedItemForMove = ref<FileEntry | null>(null)
const rootDirectoryNode = ref<DocTreeNode | undefined>(undefined)

const canReplace = computed(() => hasPermission(PermDocumentWrite))
const canDelete = computed(() => hasPermission(PermDocumentDelete))
const canAdd = computed(() => hasPermission(PermDocumentAdd))

const emit = defineEmits<{
	(e: 'select-file', file: Document): void
	(e: 'select-url', url: URL): void
}>()

const fileSelect = (document: Document) => emit('select-file', document)
const urlSelect = (url: URL) => emit('select-url', url)

const fileToBytes = (file: File): Promise<number[]> =>
	new Promise((resolve, reject) => {
		const reader = new FileReader()
		reader.onload = () => resolve(Array.from(new Uint8Array(reader.result as ArrayBuffer)))
		reader.onerror = reject
		reader.readAsArrayBuffer(file)
	})

const uploadFiles = async (files: File[], existingId?: number) => {
	if (!model.value) return
	isLoading.value = true
	for (const file of files) {
		const bytes = await fileToBytes(file)
		const req = existingId
			? { id: existingId, meta: { name: file.name, path: model.value.path, description: '' }, content: bytes }
			: { id: 0, meta: { name: file.name, path: model.value.path, description: '' }, content: bytes }

		try {
			await http.post('/api/document/update', req)

			if (existingId) {
				const doc = model.value.documents.find((d) => d.id === existingId)
				if (doc) {
					doc.name = file.name
					doc.dateAdded = new Date().toISOString()
				}
			} else {
				folderRefreshCallback.value?.(model.value.path)
			}
			historyRefreshCallback.value?.()
		} catch {}
	}
	isLoading.value = false
}

const add = async () => {
	const input = document.createElement('input')
	input.type = 'file'
	input.multiple = true
	input.setAttribute('data-test', 'document-upload')

	input.onchange = async () => input.files && uploadFiles(Array.from(input.files))
	input.click()
	historyRefreshCallback.value?.()
}

const isAddUrlOpen = ref(false)
const newUrlTitle = ref('')
const newUrlPath = ref('')

const addURL = () => {
	if (!model.value) return
	newUrlTitle.value = ''
	newUrlPath.value = ''
	isAddUrlOpen.value = true
}

const loadDirectoryTree = async () => {
	const response = await http.get<{ data: DocTreeNode }>('/api/document/tree')
	rootDirectoryNode.value = response.data.data
}

const moveToDirectory = async (item: FileEntry) => {
	if (!model.value) return
	selectedItemForMove.value = item
	await loadDirectoryTree()
	isDirectorySelectOpen.value = true
}

const handleDirectorySelected = async (newPath: string) => {
	if (!model.value || !selectedItemForMove.value) return
	isLoading.value = true

	const newFolderPath: MoveDocumentRequest = {
		id: selectedItemForMove.value.id,
		newPath,
	}

	await http.post(`/api/${selectedItemForMove.value.kind}/move`, newFolderPath)
	folderRefreshCallback.value?.(model.value.path)
	historyRefreshCallback.value?.()
	toaster.showSuccess(t('document.successfullyMoved'))

	isLoading.value = false
	closeDirectorySelect()
}

const closeDirectorySelect = () => {
	isDirectorySelectOpen.value = false
	selectedItemForMove.value = null
}

const saveNewUrl = async () => {
	if (!model.value || !newUrlPath.value.trim()) return
	isLoading.value = true
	const payload = {
		id: 0,
		meta: {
			name: newUrlTitle.value.trim(),
			url: newUrlPath.value.trim(),
			path: model.value.path,
		},
	}
	await http.post('/api/url/update', payload)
	await folderRefreshCallback.value?.(model.value.path)

	historyRefreshCallback.value?.()
	isLoading.value = false
	toaster.showSuccess(t('misc.successfullyAdded'))
	closeAddUrl()
}

const closeAddUrl = () => {
	isAddUrlOpen.value = false
	newUrlTitle.value = ''
	newUrlPath.value = ''
}

const replace = async (id: number) => {
	if (!model.value) return
	const input = document.createElement('input')
	input.type = 'file'
	input.setAttribute('data-test', 'document-replace')

	input.onchange = async () => input.files && uploadFiles([input.files[0]], id)
	input.click()
	historyRefreshCallback.value?.()
}

const showConnections = ref(false)
const connections = ref<ReferenceConnection[]>([])
const connectionsMode = ref<'view' | 'delete'>('view')
const pendingDelete = ref<{ id: number; kind: 'document' | 'url' } | null>(null)

async function fetchConnections(id: number, kind: 'document' | 'url') {
	const path = kind === 'document' ? `/api/document/${id}/references` : `/api/url/${id}/references`
	const response = await http.get<{ data: ReferenceConnection[] }>(path)
	return response.data.data ?? []
}

const openConnections = async (id: number, kind: 'document' | 'url') => {
	connections.value = await fetchConnections(id, kind)
	connectionsMode.value = 'view'
	pendingDelete.value = null
	showConnections.value = true
}

const onConfirmDelete = () => {
	if (!pendingDelete.value) return
	const { id, kind } = pendingDelete.value
	pendingDelete.value = null
	if (kind === 'document') confirmRemove(id)
	else confirmURLRemove(id)
}

const requestRemove = async (id: number, refCount: number) => {
	if (refCount > 0) {
		connections.value = await fetchConnections(id, 'document')
		connectionsMode.value = 'delete'
		pendingDelete.value = { id, kind: 'document' }
		showConnections.value = true
		return
	}
	slider.requestDeleteConfirm(t('misc.confirmDelete'), 'document', () => confirmRemove(id))
}

const confirmRemove = async (id: number) => {
	if (!model.value) return
	isLoading.value = true
	await http.delete(`/api/document/${id}`)
	model.value.documents = model.value.documents.filter((d) => d.id !== id)
	historyRefreshCallback.value?.()
	isLoading.value = false
	toaster.showSuccess(t('misc.successfullyDeleted'))
}

const requestURLRemove = async (id: number, refCount: number) => {
	if (refCount > 0) {
		connections.value = await fetchConnections(id, 'url')
		connectionsMode.value = 'delete'
		pendingDelete.value = { id, kind: 'url' }
		showConnections.value = true
		return
	}
	slider.requestDeleteConfirm(t('misc.confirmDelete'), 'url', () => confirmURLRemove(id))
}

const confirmURLRemove = async (id: number) => {
	if (!model.value) return
	isLoading.value = true
	await http.delete(`/api/url/${id}`)
	model.value.urls = (model.value.urls ?? []).filter((u) => u.id !== id)
	historyRefreshCallback.value?.()
	isLoading.value = false
	toaster.showSuccess(t('misc.successfullyDeleted'))
}

const isEditUrlOpen = ref(false)
const editUrlValue = ref('')
const editUrlName = ref('')
const editUrlTargetId = ref<number | null>(null)

const openEditUrl = (id: number, currentUrl: string, currentName: string) => {
	editUrlTargetId.value = id
	editUrlValue.value = currentUrl
	editUrlName.value = currentName
	isEditUrlOpen.value = true
}

const closeEditUrl = () => {
	isEditUrlOpen.value = false
	editUrlTargetId.value = null
	editUrlValue.value = ''
	editUrlName.value = ''
}

const saveEditUrl = async () => {
	if (!model.value || !editUrlTargetId.value || !editUrlValue.value.trim()) return
	isLoading.value = true
	await http.post('/api/url/update', {
		id: editUrlTargetId.value,
		meta: {
			name: editUrlName.value.trim(),
			url: editUrlValue.value.trim(),
			path: model.value.path,
		},
	})
	model.value.urls = (model.value.urls ?? []).map((u) =>
		u.id === editUrlTargetId.value ? { ...u, name: editUrlName.value.trim(), url: editUrlValue.value.trim() } : u,
	)
	historyRefreshCallback.value?.()
	isLoading.value = false
	toaster.showSuccess(t('misc.successfullyEdited'))
	closeEditUrl()
}

const download = async (id: number) => {
	if (!model.value) return
	const doc = model.value.documents.find((d) => d.id === id)
	if (!doc) return
	const response = await http.get(`/api/document/${id}`, { responseType: 'arraybuffer' })
	const blob = new Blob([response.data], { type: 'application/octet-stream' })
	const url = URL.createObjectURL(blob)
	const link = document.createElement('a')
	link.href = url
	link.setAttribute('download', doc.name)
	link.click()
	URL.revokeObjectURL(url)
}

const handleDrop = async (e: DragEvent) => {
	if (!model.value) return
	const files = e.dataTransfer?.files
	if (files?.length) await uploadFiles(Array.from(files))
}

type DocumentEntry = Document & { kind: 'document' }
type UrlEntry = URL & {
	kind: 'url'
	url: string
	length?: number
	dateAdded?: string | null
	author?: Document['author']
	referenceCount?: number
	instance?: Document['instance']
}

type FileEntry = DocumentEntry | UrlEntry

const showDocuments = computed(() => props.filterType !== 'url')
const showUrls = computed(() => props.filterType !== 'document')

const entries = computed<FileEntry[]>(() => {
	if (!model.value) return []
	const items: FileEntry[] = []

	if (showDocuments.value) {
		items.push(...(model.value.documents ?? []).map((d) => ({ ...d, kind: 'document' as const })))
	}

	if (showUrls.value) {
		items.push(
			...(model.value.urls ?? []).map((u) => ({
				...u,
				kind: 'url' as const,
				url: u.url ?? '',
				dateAdded: (u as any).dateAdded ?? null,
				referenceCount: u.referenceCount ?? 0,
			})),
		)
	}

	return items
})

const openLink = (path: string) => {
	if (!path) return
	window.open(toAbsoluteUrl(path), '_blank', 'noopener')
}

const isDocumentEntry = computed(
	() =>
		(item: FileEntry): item is DocumentEntry =>
			item.kind === 'document',
)
const isUrlEntry = computed(
	() =>
		(item: FileEntry): item is UrlEntry =>
			item.kind === 'url',
)

function openJournal() {
	slider.openJournal(t('document.label', 2), [{ table: 'documents' }, { table: 'urls' }])
}
</script>

<template>
	<LoadingContainer v-model="isLoading" class="rounded-container mt-3 p-3 flex-fill overflow-auto">
		<div class="d-flex flex-column overflow-hidden w-100">
			<div class="d-flex justify-content-between align-items-center gap-2">
				<h4 class="text-truncate">{{ $t('document.label', 2) + model?.path }}</h4>
				<div v-if="props.showHistory" class="d-flex justify-content-center flex-shrink-0 me-2">
					<IconButton
						:data-test="'document-history-'"
						class="bi bi-book"
						:title="$t('history.label')"
						size="fs-3"
						data-bs-toggle="tooltip"
						data-bs-placement="bottom"
						@mouseup="openJournal"
					/>
				</div>
			</div>
			<div class="table-responsive" @dragover.prevent @drop.prevent="handleDrop">
				<table class="table">
					<thead>
						<tr>
							<th v-if="props.showSelect"></th>
							<th v-if="showDocuments || showUrls">
								<i class="bi bi-file-earmark-text" v-if="showDocuments"></i>
								<i class="bi bi-link-45deg" v-if="showUrls"></i>
							</th>
							<th>{{ $t('document.name') }}</th>
							<th>{{ $t('document.length') }}</th>
							<th>{{ $t('document.added') }}</th>
							<th>{{ $t('document.addedUser') }}</th>
							<th><i class="bi bi-link" :title="$t('document.reference')"></i></th>
							<th>{{ $t('instance') }}</th>
							<th v-if="canReplace"></th>
							<th></th>
							<th></th>
							<th v-if="canDelete"></th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="(item, i) in entries" :key="`${item.kind}-${item.id}`" class="document-row" :class="{ odd: i % 2 != 0 }">
							<td v-if="props.showSelect && isDocumentEntry(item) && showDocuments">
								<input
									:id="'add-file-reference' + item.id"
									class="form-check-input"
									name="fileSelect"
									type="radio"
									:value="item.id"
									@change="fileSelect(item as Document)"
								/>
							</td>

							<td v-else-if="props.showSelect && isUrlEntry(item) && showUrls">
								<input
									:id="'add-url-reference' + item.id"
									class="form-check-input"
									name="urlSelect"
									type="radio"
									:value="item.id"
									@change="urlSelect(item as URL)"
								/>
							</td>

							<td>
								<i :class="isDocumentEntry(item) ? 'bi bi-file-earmark-text' : 'bi bi-link-45deg'"></i>
							</td>
							<td :title="isUrlEntry(item) ? item.url : item.name">
								<span class="text-break">{{
									((isDocumentEntry(item) ? item.name : item.name || item.url) || '').length > lengthToShow
										? ((isDocumentEntry(item) ? item.name : item.name || item.url) || '').slice(0, lengthToShow) + '...'
										: isDocumentEntry(item)
											? item.name
											: item.name || item.url
								}}</span>
							</td>
							<td>{{ isDocumentEntry(item) ? formatBytes(item.length) : '—' }}</td>
							<td>{{ item.dateAdded ? formatDate(item.dateAdded) : '—' }}</td>
							<td>
								<template v-if="item.author">
									<i class="bi-person-fill pie1"></i>
									<span class="ms-1"> {{ item.author.firstname }} {{ item.author.lastname }} </span>
								</template>
								<span v-else>—</span>
							</td>
							<td data-test="document-reference-count">
								<button
									v-if="(item.referenceCount ?? 0) > 0"
									type="button"
									class="btn btn-link p-0"
									@click="openConnections(item.id, item.kind)"
								>
									{{ item.referenceCount }}
								</button>
								<span v-else>0</span>
							</td>
							<td>{{ item.instance?.name ?? '—' }}</td>

							<td>
								<IconButton
									:id="'move-directory-' + item.id"
									class="bi bi-folder-symlink"
									@click="moveToDirectory(item)"
									:title="$t('document.move')"
									size="fs-5"
								/>
							</td>

							<td v-if="showDocuments && canReplace && isDocumentEntry(item)">
								<IconButton
									:id="'replace-file-' + item.id"
									class="bi bi-repeat"
									@click="replace(item.id)"
									:title="$t('document.replace')"
									size="fs-5"
								/>
							</td>
							<td v-if="showUrls && canReplace && isUrlEntry(item)">
								<IconButton
									:id="'edit-url-' + item.id"
									class="bi bi-pencil-square"
									@click="openEditUrl(item.id, item.url, item.name)"
									:title="$t('misc.edit')"
									size="fs-5"
								/>
							</td>
							<td v-if="showDocuments && canReplace && isDocumentEntry(item)">
								<IconButton
									:disabled="!item.length"
									:id="'download-file-' + item.id"
									class="bi bi-download"
									@click="download(item.id)"
									:title="$t('document.download')"
									size="fs-5"
								/>
							</td>
							<td v-if="showUrls && canReplace && isUrlEntry(item)">
								<IconButton
									:id="'open-url-' + item.id"
									class="bi bi-box-arrow-up-right"
									@click="openLink(item.url)"
									:title="item.url"
									size="fs-5"
								/>
							</td>
							<td v-if="showDocuments && canDelete && isDocumentEntry(item)">
								<IconButton
									:id="'delete-file-' + item.id"
									class="bi bi-trash"
									@click="requestRemove(item.id, item.referenceCount ?? 0)"
									:title="$t('misc.delete')"
									size="fs-5"
								/>
							</td>
							<td v-if="showUrls && canDelete && isUrlEntry(item)">
								<IconButton
									:id="'delete-url-' + item.id"
									class="bi bi-trash"
									@click="requestURLRemove(item.id, item.referenceCount ?? 0)"
									:title="$t('misc.delete')"
									size="fs-5"
								/>
							</td>
						</tr>
					</tbody>
				</table>
			</div>
			<div class="row mt-2">
				<div class="col text-end">
					<div v-one-tooltip="!viewing ? $t('document.cantAdd') : !canAdd ? $t('document.cantAdd2') : ''" class="d-inline-block">
						<button
							v-if="showDocuments"
							data-test="AddNewDocumentBtn"
							id="add-file"
							type="button"
							class="btn btn-outline-secondary d-inline-flex align-items-center gap-1 me-2"
							:title="$t('misc.add')"
							@click="add"
							:disabled="!viewing || !canAdd"
						>
							<i class="bi bi-file-earmark-plus btn-icon-lg"></i>
							<span>{{ $t('document.add') }}</span>
						</button>

						<button
							v-if="showUrls"
							data-test="AddNewURLBtn"
							id="add-url"
							type="button"
							class="btn btn-outline-secondary d-inline-flex align-items-center gap-1"
							:title="$t('document.addURL')"
							@click="addURL"
							:disabled="!viewing || !canAdd"
						>
							<i class="bi bi-link-45deg btn-icon-lg"></i>
							<span>{{ $t('document.addURL') }}</span>
						</button>
					</div>
				</div>
			</div>
		</div>
	</LoadingContainer>

	<div v-if="isAddUrlOpen" class="modal-backdrop">
		<div class="modal modal-content p-3">
			<h5 class="mb-3">{{ $t('document.addURL') }}</h5>
			<label class="form-label">{{ $t('document.name') }}</label>
			<input data-test="add-new-url-name" v-model="newUrlTitle" type="text" class="form-control mb-3" />
			<label class="form-label">{{ $t('document.url') }}</label>
			<input data-test="add-new-url-input" v-model="newUrlPath" type="text" class="form-control mb-3" />
			<div class="d-flex justify-content-end gap-2">
				<button class="btn btn-secondary" @click="closeAddUrl">{{ $t('misc.cancel') }}</button>
				<button data-test="save-new-url-btn" class="btn btn-primary" :disabled="!newUrlPath" @click="saveNewUrl">
					{{ $t('misc.save') }}
				</button>
			</div>
		</div>
	</div>

	<div v-if="isEditUrlOpen" class="modal-backdrop">
		<div class="modal modal-content p-3">
			<h5 class="mb-3">{{ $t('document.editUrl') }}</h5>
			<label class="form-label">{{ $t('document.name') }}</label>
			<input data-test="edit-url-name" v-model="editUrlName" type="text" class="form-control mb-3" />
			<label class="form-label">{{ $t('document.url') }}</label>
			<input data-test="edit-url-input" v-model="editUrlValue" type="text" class="form-control mb-3" />
			<div class="d-flex justify-content-end gap-2">
				<button class="btn btn-secondary" @click="closeEditUrl">{{ $t('misc.cancel') }}</button>
				<button data-test="update-url-btn" class="btn btn-primary" :disabled="!editUrlValue" @click="saveEditUrl">
					{{ $t('misc.save') }}
				</button>
			</div>
		</div>
	</div>

	<DirectorySelectDialog
		:is-open="isDirectorySelectOpen"
		:title="t('document.selectNewDirectory')"
		:root-node="rootDirectoryNode"
		@close="closeDirectorySelect"
		@select="handleDirectorySelected"
	/>

	<ConnectionsModal v-model="showConnections" :connections="connections" :mode="connectionsMode" @confirm="onConfirmDelete" />
</template>

<style lang="sass" scoped>
td:hover
  cursor: pointer

.modal-backdrop
  position: fixed
  inset: 0
  background: rgba(0,0,0,0.45)
  display: flex
  align-items: center
  justify-content: center
  z-index: 1050

.modal-content
  border-radius: .5rem
  width: min(420px, calc(100% - 2rem))
  height: auto
</style>
